import { useEffect, useState } from "react";
import { useGraphToken } from "../auth/useGraphToken";
import { fetchAllListItems, INITIATIVE_LIST_ID, LIBRARY_LIST_ID, mapInitiative, mapLibrary } from "../api/graph";
import { BUSINESS_GROUPS, groupOf, type BusinessGroupId } from "../data/businessGroups";
import type { InitiativeItem, LibraryItem } from "../types/navigator";

/** Where an initiative is: in use, being built, or still an idea. Closed
 *  without a build (not needed, already available, cancelled) is left out. */
export type WorkStage = "live" | "building" | "idea";

export function workStage(status: string): WorkStage | null {
  if (status === "Closed - AI solution built") return "live";
  if (/^(design|development|testing)$/i.test(status.trim())) return "building";
  if (/^closed|cancel/i.test(status.trim())) return null;
  return "idea";
}

export type GroupWork = {
  live: InitiativeItem[];
  building: InitiativeItem[];
  ideas: InitiativeItem[];
  /** Published library tools for this group's departments. */
  tools: LibraryItem[];
  regions: string[];
};

export type BusinessGroupsData = {
  byGroup: Record<BusinessGroupId, GroupWork>;
  /** Published tools meant for everyone (no specific department). */
  companyWide: LibraryItem[];
};

const newest = (a: { modified?: string }, b: { modified?: string }) =>
  (b.modified ?? "").localeCompare(a.modified ?? "");

export function buildBusinessGroups(initiatives: InitiativeItem[], library: LibraryItem[]): BusinessGroupsData {
  const byGroup = {} as Record<BusinessGroupId, GroupWork>;
  for (const g of BUSINESS_GROUPS) byGroup[g.id] = { live: [], building: [], ideas: [], tools: [], regions: [] };

  for (const item of initiatives) {
    const group = groupOf(item.team);
    const stage = workStage(item.status);
    if (!group || !stage) continue;
    const work = byGroup[group];
    (stage === "live" ? work.live : stage === "building" ? work.building : work.ideas).push(item);
    const region = item.region.trim();
    if (region && !work.regions.includes(region)) work.regions.push(region);
  }

  const companyWide: LibraryItem[] = [];
  // Non-admins only see Published tools, so the dashboard counts only those.
  for (const tool of library.filter((t) => t.status === "Published")) {
    const group = groupOf(tool.department);
    if (group) byGroup[group].tools.push(tool);
    else companyWide.push(tool);
  }

  for (const work of Object.values(byGroup)) {
    work.live.sort(newest);
    work.building.sort(newest);
    work.ideas.sort(newest);
    work.tools.sort((a, b) => b.likes - a.likes || a.title.localeCompare(b.title));
  }
  companyWide.sort((a, b) => b.likes - a.likes || a.title.localeCompare(b.title));
  return { byGroup, companyWide };
}

/** AI work per business group, worked out from AI Initiatives and the AI
 *  Library on every load, so it never needs updating by hand. */
export function useBusinessGroups(): { data: BusinessGroupsData | null; loading: boolean; error: string | null } {
  const getToken = useGraphToken();
  const [state, setState] = useState<{ data: BusinessGroupsData | null; loading: boolean; error: string | null }>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const token = await getToken();
        const [initiatives, library] = await Promise.all([
          fetchAllListItems(token, INITIATIVE_LIST_ID, mapInitiative),
          fetchAllListItems(token, LIBRARY_LIST_ID, mapLibrary),
        ]);
        if (alive) setState({ data: buildBusinessGroups(initiatives, library), loading: false, error: null });
      } catch (err) {
        if (alive) setState({ data: null, loading: false, error: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => {
      alive = false;
    };
  }, [getToken]);

  return state;
}
